import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, Boxes, ChevronRight, FileText, Download } from "lucide-react";
import palletBasePath from "@assets/Screenshot_2026-05-04_at_13.36.27_1777919818299.png";
import eabAcImg from "@assets/IMG_1071_1777859685181.jpeg";
import { formatMoney } from "@shared/currency";
import { optimizedSrcSet } from "@/lib/imageOptimize";

type CompositionItem = {
  id: string;
  modelId: string;
  brand: string | null;
  mfgSku: string | null;
  imageUrl: string | null;
  additionalImages: string[] | null;
  description: string | null;
  descriptor: string | null;
  color: string | null;
  boxType: string | null;
  boxTypeDescription: string | null;
  features: string[] | null;
  attributes: Record<string, any> | null;
  relatedDocuments: Array<{ name: string; url: string }> | null;
  quantity: number;
  unitPriceCents: number;
  lineTotalCents: number;
  sortOrder: number;
};

type CompositionResponse = {
  definition: {
    id: string;
    palletNumber: number;
    name: string;
    grade: string;
    totalUnits: number;
    totalPriceCents: number;
  };
  items: CompositionItem[];
};

const BOX_TYPE_LABEL: Record<string, string> = {
  EOB: "Original brown box",
  EGB: "Generic brown box",
  EAB: "Brown box w/ artwork",
};

function formatCents(cents: number) {
  return formatMoney(cents / 100);
}

function pickAttr(attrs: Record<string, any> | null, ...keys: string[]) {
  if (!attrs || typeof attrs !== "object") return null;
  for (const k of keys) {
    if (attrs[k] != null && String(attrs[k]).trim() !== "") {
      return String(attrs[k]);
    }
  }
  return null;
}

export function PalletCompositionCard({ palletModelId }: { palletModelId: string }) {
  const [selectedItem, setSelectedItem] = useState<CompositionItem | null>(null);
  const { data, isLoading, isError } = useQuery<CompositionResponse>({
    queryKey: ["/api/pallets", palletModelId, "composition"],
  });

  if (isLoading) {
    return (
      <Card data-testid="pallet-composition-loading">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Couldn't load pallet contents right now.
        </CardContent>
      </Card>
    );
  }

  const totalUnits = data.items.reduce((sum, it) => sum + it.quantity, 0);

  return (
    <>
    <Card data-testid={`pallet-composition-${palletModelId}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Boxes className="h-5 w-5 text-blue-600" />
          What's in this pallet
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {data.items.length} different model{data.items.length === 1 ? "" : "s"} · {totalUnits} units total · all B-grade
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {[...data.items].sort((a, b) => b.quantity - a.quantity).map((item) => {
          const capacity = pickAttr(item.attributes, "btu", "BTU", "capacity", "btus");
          const feature = item.features?.[0] ?? null;
          return (
            <div
              key={item.id}
              className="flex gap-3 sm:gap-4 p-3 border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-1"
              data-testid={`pallet-line-${item.modelId}`}
              onClick={() => setSelectedItem(item)}
              role="button"
              tabIndex={0}
              aria-label={`View details for ${item.brand ?? ''} ${item.mfgSku ?? ''}, ${item.quantity} pieces`}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedItem(item); } }}
            >
              <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                {item.imageUrl ? (
                  <picture>
                    <source
                      type="image/webp"
                      srcSet={optimizedSrcSet(item.imageUrl, [200, 400]) || undefined}
                      sizes="96px"
                    />
                    <img
                      src={item.imageUrl}
                      alt={`${item.brand} ${item.mfgSku}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                ) : (
                  <Package className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p
                      className="font-semibold text-sm sm:text-base truncate"
                      data-testid={`pallet-line-brand-${item.modelId}`}
                    >
                      {item.brand}
                    </p>
                    <p
                      className="text-xs sm:text-sm text-muted-foreground font-mono truncate"
                      data-testid={`pallet-line-sku-${item.modelId}`}
                    >
                      {item.mfgSku}
                      {item.descriptor && (
                        <span
                          className="ml-1.5 font-sans text-foreground/80"
                          data-testid={`pallet-line-descriptor-${item.modelId}`}
                        >
                          {item.descriptor}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-1.5">
                    <Badge
                      className="text-xs font-bold whitespace-nowrap bg-amber-800 hover:bg-amber-900 text-white"
                      data-testid={`pallet-line-qty-${item.modelId}`}
                    >
                      {item.quantity} pcs
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {capacity && (
                    <Badge
                      variant="outline"
                      className="text-[10px] sm:text-xs font-normal"
                    >
                      {capacity} BTU
                    </Badge>
                  )}
                  {item.color && (
                    <Badge
                      variant="outline"
                      className="text-[10px] sm:text-xs font-normal"
                    >
                      {item.color}
                    </Badge>
                  )}
                  {item.boxType && (
                    <Badge
                      variant="outline"
                      className="text-[10px] sm:text-xs font-normal text-blue-700 border-blue-300 bg-blue-50"
                      title={item.boxTypeDescription ?? BOX_TYPE_LABEL[item.boxType] ?? item.boxType}
                    >
                      {item.boxType}
                    </Badge>
                  )}
                </div>
                {feature && (
                  <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
                    {feature}
                  </p>
                )}
                <div className="mt-auto pt-2 flex items-baseline justify-between gap-2 text-xs sm:text-sm">
                  <span className="text-muted-foreground">
                    {formatCents(item.unitPriceCents)} each
                  </span>
                  <span
                    className="font-semibold text-foreground"
                    data-testid={`pallet-line-total-${item.modelId}`}
                  >
                    {formatCents(item.lineTotalCents)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        <div className="flex items-baseline justify-between pt-3 border-t mt-2">
          <span className="text-sm sm:text-base font-medium text-muted-foreground">
            Pallet total
          </span>
          <span
            className="text-lg sm:text-xl font-bold text-foreground"
            data-testid="pallet-grand-total"
          >
            {formatCents(data.definition.totalPriceCents)}
          </span>
        </div>
      </CardContent>
    </Card>

    <ConstituentDetailModal
      item={selectedItem}
      open={selectedItem !== null}
      onClose={() => setSelectedItem(null)}
    />
    </>
  );
}

function ConstituentDetailModal({
  item,
  open,
  onClose,
}: {
  item: CompositionItem | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!item) return null;

  const allImages = [item.imageUrl, ...(item.additionalImages || [])].filter(Boolean) as string[];
  const capacity = pickAttr(item.attributes, "btu", "BTU", "capacity", "btus");
  const attrs = item.attributes || {};
  const specKeys = Object.keys(attrs).filter(
    (k) => attrs[k] != null && String(attrs[k]).trim() !== "" && !["btu", "BTU", "capacity", "btus"].includes(k)
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`constituent-detail-modal-${item.modelId}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{item.brand} {item.mfgSku}</span>
            <Badge className="bg-amber-800 hover:bg-amber-900 text-white text-xs font-bold">
              {item.quantity} pcs
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {allImages.length > 0 && (
          <div className="space-y-2" data-testid="constituent-images">
            <div
              className="w-full aspect-square rounded-md overflow-hidden bg-muted border flex items-center justify-center"
              data-testid="constituent-main-image-wrap"
            >
              <picture>
                <source
                  type="image/webp"
                  srcSet={optimizedSrcSet(allImages[0], [400, 800]) || undefined}
                  sizes="(max-width: 640px) 100vw, 512px"
                />
                <img
                  src={allImages[0]}
                  alt={`${item.brand} ${item.mfgSku}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain"
                  data-testid="constituent-image-0"
                />
              </picture>
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="constituent-thumbnails">
                {allImages.slice(1).map((url, i) => (
                  <div
                    key={i + 1}
                    className="shrink-0 w-16 h-16 rounded-md overflow-hidden bg-muted border flex items-center justify-center"
                  >
                    <picture>
                      <source
                        type="image/webp"
                        srcSet={optimizedSrcSet(url, [200, 400]) || undefined}
                        sizes="64px"
                      />
                      <img
                        src={url}
                        alt={`${item.brand} ${item.mfgSku} image ${i + 2}`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain"
                        data-testid={`constituent-image-${i + 1}`}
                      />
                    </picture>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {item.relatedDocuments && item.relatedDocuments.length > 0 && (
          <div data-testid="constituent-documents">
            <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Documents
            </h4>
            <div className="flex flex-col gap-1.5">
              {item.relatedDocuments.map((doc, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-md border bg-secondary/30 hover:bg-secondary/60 transition-colors"
                  data-testid={`constituent-document-row-${i}`}
                >
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center gap-2 px-2.5 py-1.5 text-sm font-medium text-foreground hover:underline min-w-0"
                    data-testid={`constituent-document-link-${i}`}
                  >
                    <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <span className="truncate">{doc.name}</span>
                  </a>
                  <a
                    href={doc.url}
                    download={doc.name}
                    className="flex items-center justify-center h-8 w-8 mr-1 rounded-md hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                    title={`Download ${doc.name}`}
                    aria-label={`Download ${doc.name}`}
                    data-testid={`constituent-document-download-${i}`}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {item.description && (
          <div data-testid="constituent-description">
            <p className="text-sm text-foreground leading-relaxed">{item.description}</p>
          </div>
        )}

        {(item.features && item.features.length > 0) && (
          <div data-testid="constituent-features">
            <h4 className="text-sm font-semibold mb-1.5">Features</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-4">
              {item.features.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        {(capacity || specKeys.length > 0 || item.color || item.boxType) && (
          <div data-testid="constituent-specs">
            <h4 className="text-sm font-semibold mb-1.5">Specifications</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {capacity && (
                <>
                  <span className="text-muted-foreground">BTU</span>
                  <span className="font-medium">{capacity}</span>
                </>
              )}
              {item.color && (
                <>
                  <span className="text-muted-foreground">Color</span>
                  <span className="font-medium">{item.color}</span>
                </>
              )}
              {item.boxType && (
                <>
                  <span className="text-muted-foreground">Box Type</span>
                  <span className="font-medium">{item.boxTypeDescription ?? BOX_TYPE_LABEL[item.boxType] ?? item.boxType}</span>
                </>
              )}
              {specKeys.map((k) => (
                <span key={k} className="contents">
                  <span className="text-muted-foreground capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span className="font-medium">{String(attrs[k])}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-baseline justify-between pt-2 border-t text-sm" data-testid="constituent-pricing">
          <span className="text-muted-foreground">{formatCents(item.unitPriceCents)} each × {item.quantity}</span>
          <span className="font-semibold text-base">{formatCents(item.lineTotalCents)}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PalletCompositeHero({
  palletModelId,
  alt,
  heroImageUrl,
}: {
  palletModelId: string;
  alt: string;
  /** Optional admin-uploaded photo of this exact pallet. When present we
   *  show it in place of the synthetic 4-tile composite. */
  heroImageUrl?: string | null;
}) {
  const { data, isLoading } = useQuery<CompositionResponse>({
    queryKey: ["/api/pallets", palletModelId, "composition"],
  });

  // Prefer the admin-uploaded hero (definition.heroImageUrl) when present so
  // we show a real photo of the assembled pallet instead of the constituent
  // image grid. Fall back to whichever path the caller passed in (inventory
  // enrichment) so the photo appears even before the composition query
  // resolves.
  const effectiveHero = heroImageUrl || data?.definition?.heroImageUrl || null;

  if (effectiveHero) {
    // Outer wrapper enforces the square frame so a wide PDP column can't
    // stretch the hero horizontally. The img fills the square and uses
    // object-contain to letterbox non-square source photos (common with
    // admin-uploaded SharkNinja pallet photos).
    return (
      <div className="w-full max-w-md mx-auto" data-testid="pallet-composite-hero">
        <div className="w-full bg-white rounded-md overflow-hidden" style={{ aspectRatio: '1 / 1' }}>
          <picture>
            <source
              type="image/webp"
              srcSet={optimizedSrcSet(effectiveHero, [400, 800, 1600]) || undefined}
              sizes="(max-width: 640px) 100vw, 448px"
            />
            <img
              src={effectiveHero}
              alt={alt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain bg-stone-50"
              data-testid="pallet-hero-uploaded"
            />
          </picture>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="aspect-square w-full bg-muted animate-pulse rounded-md flex items-center justify-center">
          <Boxes className="h-12 w-12 text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Match the "What's in this pallet" list ordering (qty desc) so the hero
  // grid reads top-to-bottom, left-to-right in the same sequence as the rows
  // below it.
  const images = [...data.items]
    .sort((a, b) => b.quantity - a.quantity)
    .filter((i) => i.imageUrl)
    .slice(0, 16)
    .map((i) => i.imageUrl!);
  const totalUnits = data.definition.totalUnits || data.items.reduce((s, it) => s + it.quantity, 0);
  // Cheapest constituent unit price → "from $X per item" line, mirroring the
  // showroom landing tile copy.
  const perItemMinCents = data.items.reduce<number | null>((min, it) => {
    const c = Number(it.unitPriceCents);
    if (!Number.isFinite(c) || c <= 0) return min;
    return min == null || c < min ? c : min;
  }, null);

  return (
    // Cap max-width so the pallet hero scales proportionally on large screens
    // instead of stretching to fill the column. The image area is wrapped in
    // an explicit aspect-square frame so the synthetic constituent grid
    // matches the square presentation used by `PalletMiniHero` on showroom
    // catalog cards.
    <div className="w-full max-w-md mx-auto" data-testid="pallet-composite-hero">
      <PalletImageGrid images={images} />
      <div className="flex justify-center items-center gap-2 py-1.5 flex-wrap">
        <PalletBadge totalUnits={totalUnits} modelCount={data.items.length} />
      </div>
      {perItemMinCents != null && (
        <p
          className="text-center text-xs text-muted-foreground pb-2"
          data-testid="pallet-per-item-price"
        >
          from {formatCents(perItemMinCents)} per item
        </p>
      )}
    </div>
  );
}

function PalletBadge({ totalUnits, modelCount }: { totalUnits?: number; modelCount?: number }) {
  const parts: string[] = [];
  if (totalUnits != null && totalUnits > 0) parts.push(`${totalUnits} pcs`);
  if (modelCount != null && modelCount > 0) parts.push(`${modelCount} SKU${modelCount === 1 ? '' : 's'}`);
  if (parts.length === 0) return null;

  return (
    <Badge className="bg-amber-800 hover:bg-amber-900 text-white shadow-md text-xs" data-testid="pallet-badge">
      <Boxes className="h-3 w-3 mr-1" />
      {parts.join(' · ')}
    </Badge>
  );
}

function PalletBadgeMini({ totalUnits, modelCount }: { totalUnits?: number; modelCount?: number }) {
  const parts: string[] = [];
  if (totalUnits != null && totalUnits > 0) parts.push(`${totalUnits} pcs`);
  if (modelCount != null && modelCount > 0) parts.push(`${modelCount} SKU${modelCount === 1 ? '' : 's'}`);
  if (parts.length === 0) return null;

  return (
    <Badge className="bg-amber-800 hover:bg-amber-900 text-white shadow text-[10px] px-1.5 py-0.5" data-testid="pallet-badge">
      <Boxes className="h-2.5 w-2.5 mr-0.5" />
      {parts.join(' · ')}
    </Badge>
  );
}

type PalletRowSpec = { count: number; offset: number };

/**
 * Layout map for `n` constituent images (1–16). `cols` is the column unit and
 * each row's `offset` is in *column units* (supports half-step centering, e.g.
 * 0.5 to nudge a 1-tile row into the centre of a 2-col grid). Partial rows
 * are always centred.
 *
 * For n=8 we use 3-3-2 (3 cols, 2 centred on the bottom) instead of 4×2 —
 * keeps tiles roughly the same scale as the surrounding 7- and 9-tile pallet
 * layouts so an 8-SKU pallet doesn't suddenly read denser than a 9-SKU one.
 */
export function palletGridLayout(n: number): { cols: number; rows: PalletRowSpec[] } {
  if (n <= 1) return { cols: 1, rows: [{ count: 1, offset: 0 }] };
  if (n === 2) return { cols: 2, rows: [{ count: 2, offset: 0 }] };
  if (n === 3) return { cols: 2, rows: [{ count: 1, offset: 0.5 }, { count: 2, offset: 0 }] };
  if (n === 4) return { cols: 2, rows: [{ count: 2, offset: 0 }, { count: 2, offset: 0 }] };
  if (n === 5) return {
    cols: 2,
    rows: [{ count: 1, offset: 0.5 }, { count: 2, offset: 0 }, { count: 2, offset: 0 }],
  };
  if (n === 6) return { cols: 3, rows: [{ count: 3, offset: 0 }, { count: 3, offset: 0 }] };
  if (n === 7) return {
    cols: 3,
    rows: [{ count: 1, offset: 1 }, { count: 3, offset: 0 }, { count: 3, offset: 0 }],
  };
  if (n === 8) return {
    cols: 3,
    rows: [{ count: 3, offset: 0 }, { count: 3, offset: 0 }, { count: 2, offset: 0.5 }],
  };
  if (n === 9) return {
    cols: 3,
    rows: [{ count: 3, offset: 0 }, { count: 3, offset: 0 }, { count: 3, offset: 0 }],
  };
  if (n === 10) return {
    cols: 3,
    rows: [
      { count: 3, offset: 0 },
      { count: 3, offset: 0 },
      { count: 3, offset: 0 },
      { count: 1, offset: 1 },
    ],
  };
  if (n === 11) return {
    cols: 3,
    rows: [
      { count: 3, offset: 0 },
      { count: 3, offset: 0 },
      { count: 3, offset: 0 },
      { count: 2, offset: 0.5 },
    ],
  };
  if (n === 12) return {
    cols: 4,
    rows: [{ count: 4, offset: 0 }, { count: 4, offset: 0 }, { count: 4, offset: 0 }],
  };
  // 13–16: 4×4 with last row partially filled and centred.
  const last = Math.min(n, 16) - 12;
  return {
    cols: 4,
    rows: [
      { count: 4, offset: 0 },
      { count: 4, offset: 0 },
      { count: 4, offset: 0 },
      { count: last, offset: (4 - last) / 2 },
    ],
  };
}

export function PalletImageGrid({
  images,
  className,
  fill,
}: {
  images: string[];
  className?: string;
  fill?: boolean;
}) {
  const capped = images.slice(0, 16);
  const n = capped.length;
  // Outer pallet (tiles + wooden base) is always a square. In `fill` mode
  // (catalog cards) the parent provides a fixed height and we centre via
  // `mx-auto`. Standalone usage (PDP hero) is width-driven.
  const sizeClass = fill ? 'h-full aspect-square mx-auto' : 'w-full';

  return (
    <div
      className={`${sizeClass} bg-stone-100 overflow-hidden flex flex-col ${className || ''}`}
      style={{
        ...(fill ? {} : { aspectRatio: '1 / 1' }),
      }}
    >
      <div className="flex-1 min-h-0 flex items-end justify-center px-2 pt-2 pb-0">
        {n === 0 ? (
          <div className="flex-1 self-stretch flex items-center justify-center bg-amber-900/20 rounded-sm min-h-0 border border-amber-900/40">
            <Boxes className="h-16 w-16 text-amber-900/60" />
          </div>
        ) : (
          <PalletTileGrid images={capped} />
        )}
      </div>
      <div
        className="shrink-0 w-full"
        style={{ height: 'clamp(14px, 10%, 28px)' }}
      >
        <img
          src={palletBasePath}
          alt="Wooden pallet base"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-fill block"
          data-testid="pallet-base-image"
        />
      </div>
    </div>
  );
}

function PalletTileGrid({ images }: { images: string[] }) {
  const n = images.length;
  const { cols, rows } = palletGridLayout(n);
  const totalRows = rows.length;
  // Double the column count internally so half-step offsets (0.5) snap to a
  // real CSS-grid line. Each tile spans 2 internal columns.
  const internalCols = cols * 2;
  const gap = 4;

  const cells: Array<{ url: string; idx: number; startCol: number; row: number }> = [];
  let imgIdx = 0;
  rows.forEach((rowSpec, rowIdx) => {
    for (let i = 0; i < rowSpec.count; i++) {
      const startCol = 1 + Math.round(rowSpec.offset * 2) + i * 2;
      cells.push({ url: images[imgIdx], idx: imgIdx, startCol, row: rowIdx + 1 });
      imgIdx++;
    }
  });

  // Aspect ratio of the grid wrapper is cols/totalRows so every cell renders
  // as the same square: with internalCols = cols*2 and each tile spanning 2
  // internal cols, cell width = (W - gaps) / cols and cell height =
  // (H - gaps) / totalRows. We use the SAME track size in both directions
  // (`minmax(0, 1fr)`) so all tiles grow and shrink together as a single unit.
  return (
    <div
      style={{
        aspectRatio: `${cols} / ${totalRows}`,
        maxWidth: '100%',
        maxHeight: '100%',
        width: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(${internalCols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${totalRows}, minmax(0, 1fr))`,
        gap: `${gap}px`,
      }}
    >
      {cells.map((cell) => (
        <div
          key={cell.idx}
          style={{
            gridColumn: `${cell.startCol} / span 2`,
            gridRow: `${cell.row}`,
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <ImageTile url={cell.url} idx={cell.idx} />
        </div>
      ))}
    </div>
  );
}

function ImageTile({ url, idx }: { url: string; idx: number }) {
  // Cell wrapper is already square (guaranteed by PalletTileGrid math), so
  // the tile fills it and the product image is inset to ~90% with a uniform
  // warm brown crate background.
  return (
    <div
      className="w-full h-full flex items-center justify-center bg-amber-900/25 overflow-hidden border border-amber-900/50"
      data-testid={`pallet-hero-tile-${idx}`}
    >
      <img
        src={url}
        alt="Pallet item"
        width={120}
        height={120}
        loading="lazy"
        decoding="async"
        className="max-w-[90%] max-h-[90%] object-contain"
      />
    </div>
  );
}

export function PalletMiniHero({
  images,
  modelCount,
  totalUnits,
  className,
  badgeSlot,
  heroImageUrl,
}: {
  images: string[];
  modelCount?: number;
  totalUnits?: number;
  className?: string;
  /** Optional extra badge (e.g. "Factory Refurbished") rendered next to the
   *  "N pcs · M SKUs" pill instead of overlapping the image. */
  badgeSlot?: ReactNode;
  /** Admin-uploaded photo of the assembled pallet — when present, replaces
   *  the synthetic constituent grid on catalog cards. */
  heroImageUrl?: string | null;
}) {
  return (
    <div className={`w-full h-full bg-white flex flex-col ${className || ''}`} data-testid="pallet-mini-hero">
      <div className="flex-1 min-h-0">
        {heroImageUrl ? (
          <img
            src={heroImageUrl}
            alt="Pallet"
            width={400}
            height={400}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain bg-stone-100"
            data-testid="pallet-mini-hero-uploaded"
          />
        ) : (
          <PalletImageGrid images={images} fill />
        )}
      </div>
      {(modelCount != null || totalUnits != null || badgeSlot) && (
        <div className="flex justify-center items-center gap-1.5 py-0.5 bg-stone-100 shrink-0 flex-wrap">
          <PalletBadgeMini totalUnits={totalUnits} modelCount={modelCount} />
          {badgeSlot}
        </div>
      )}
    </div>
  );
}

export function PalletPackagingExample() {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <>
    <Card className="overflow-hidden" data-testid="pallet-packaging-example">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Package className="h-4 w-4 text-amber-700" />
          Packaging
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="relative rounded-lg overflow-hidden bg-stone-50 border cursor-pointer group"
          data-testid="packaging-example-image"
          onClick={() => setLightboxOpen(true)}
          role="button"
          tabIndex={0}
          aria-label="View full-size packaging photo"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightboxOpen(true); } }}
        >
          <img
            src={eabAcImg}
            alt="Example packaging — brown carton with product label"
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-contain max-h-[220px]"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="text-white/70 font-bold text-3xl sm:text-4xl tracking-widest uppercase select-none"
              style={{ transform: 'rotate(-20deg)', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
            >
              EXAMPLE
            </span>
          </div>
          <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Click to enlarge
          </div>
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed" data-testid="packaging-description">
          Each unit ships in a sealed brown carton with a manufacturer label identifying the model, specs, and condition.
          Box condition may vary — some may show minor warehouse handling marks.
        </p>
      </CardContent>
    </Card>

    <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
      <DialogContent className="max-w-3xl p-2 sm:p-4" data-testid="packaging-lightbox">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4 text-amber-700" />
            Packaging Example
          </DialogTitle>
        </DialogHeader>
        <div className="relative rounded-lg overflow-hidden bg-stone-50">
          <img
            src={eabAcImg}
            alt="Example packaging — brown carton with product label (full size)"
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-contain"
            data-testid="packaging-lightbox-image"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="text-white/50 font-bold text-5xl sm:text-6xl tracking-widest uppercase select-none"
              style={{ transform: 'rotate(-20deg)', textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
            >
              EXAMPLE
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
